import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const budget = {
    screens: {
        index: 'Budget',
        form: 'Budget',
        detail: 'Budget Details',
        history: 'History',
    },
    incomeTarget: {
        section: 'Income goals',
    },
    list: {
        emptyTitle: 'No budgets',
        emptySubtitle: 'Set limits by category to track what you\'ve spent this month.',
        emptyAction: 'Create budget',
        byCategory: 'By category',
        spendingSection: 'Spending',
    },
    summary: {
        budgeted: 'Budgeted',
        spent: 'Spent',
        overLimit_one: '{{count}} category over limit',
        overLimit_other: '{{count}} categories over limit',
        target: 'Target',
        received: 'Received',
        incomeToGo: '{{amount}} to go',
        incomeReached: 'Target reached',
    },
    card: {
        progress: '{{spent}} / {{amount}}',
        progressWithCategories_one: '{{spent}} / {{amount}} · {{count}} category',
        progressWithCategories_other: '{{spent}} / {{amount}} · {{count}} categories',
        overBudget: 'over budget',
        remaining: 'remaining',
        goalReached: 'goal reached',
        toGo: 'to go',
        originsCount: '{{originCount}} sources',
        subcategoriesCount: '{{subcategoryCount}} subcategories',
    },
    form: {
        titleNew: 'New Budget',
        titleEdit: 'Edit Budget',
        amountLabel: 'Budget amount',
        amountLabelIncome: 'Monthly income goal',
        typeLabel: 'Type',
        type: {
            expense: 'Expense',
            income: 'Income',
        },
        typeIncomeHint: 'Income goal: income from the chosen categories counts toward the progress automatically (e.g. Uber and 99 payouts).',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. Essentials',
        categoriesLabel: 'Categories',
        categoriesSelected_one: '{{count}} selected',
        categoriesSelected_other: '{{count}} selected',
        categoriesPlaceholder: 'Select categories...',
        colorLabel: 'Color',
        iconLabel: 'Icon',
        periodLabel: 'Period',
        cycleLabel: 'Cycle',
        period: {
            monthly: 'Monthly',
            biweekly: 'Every 2 weeks',
            weekly: 'Weekly',
        },
        cycleStartDayLabel: 'Starts on day',
        cycleStartDayCalendar: 'Day 1 (whole month)',
        cycleStartDayValue: 'Day {{day}}',
        cycleStartDaySheetTitle: 'Start day',
        cycleStartDayHint: 'The cycle runs from this day to the day before it next month. To follow a card statement, use the closing day + 1; to follow a paycheck, the day it lands.',
        cycleWeekdayLabel: 'Week starts on',
        cycleAnchorLabel: 'Reference fortnight',
        cycleAnchorHint: 'The other fortnights are counted every 14 days from this date, forwards and backwards.',
        cyclePreview: 'Current cycle: {{range}}.',
        originsLabel: 'Where',
        originsPlaceholder: 'All accounts and cards',
        originsSummary: '{{name}} and {{extra}} more',
        subcategoriesLabel: 'Subcategories',
        subcategoriesAll: 'All',
        subcategoriesOnly: 'Only {{name}}',
        subcategoriesOnlyMore: 'Only {{name}} and {{extra}} more',
        subcategoriesNoLimits: 'no limits',
        subcategoriesLimits_one: '{{count}} limit',
        subcategoriesLimits_other: '{{count}} limits',
        subcategoryLimitsOverflow: 'Your limits go {{amount}} over the cap',
        deleteButton: 'Delete budget',
        saveA11yCreate: 'Create budget',
        saveA11ySave: 'Save',
        colorSheetTitle: 'Choose a color',
        iconSheetTitle: 'Choose an icon',
    },
    deleteDialog: {
        title: 'Delete budget',
        message: 'Are you sure? The budget will be removed, but transactions will be kept.',
        confirm: 'Delete',
    },
    originsSheet: {
        title: 'Where this budget applies',
        subtitle: 'Optional. With nothing selected it applies to every account and card.',
        searchPlaceholder: 'Search account or card...',
        accounts: 'Accounts',
        cards: 'Cards',
        all: 'All accounts and cards',
        confirm: 'Confirm',
        clear: 'Clear selection',
        archived: 'Archived',
    },
    categoriesSheet: {
        title: 'Budget categories',
        subtitle: 'Choose one or more. The same category can be in more than one budget.',
        searchPlaceholder: 'Search category...',
        alreadyUsed: 'Already used in another budget',
        confirm: 'Confirm ({{selectedCount}})',
        selectAtLeastOne: 'Select at least one',
    },
    subcategoriesSheet: {
        title: 'Subcategories',
        subtitle: 'Uncheck what does not belong in this budget. Optionally, set aside a limit per line. At least one stays checked per category.',
        includeA11y: 'Include {{name}} in the budget',
        excludeA11y: 'Remove {{name}} from the budget',
        reserved: '{{amount}} set aside',
        free: '{{amount}} free',
        setLimit: 'Set limit',
        residual: 'Everything else',
        residualHint: 'Calculated automatically',
        empty: 'The chosen categories have no subcategories.',
        confirm: 'Confirm',
        clearA11y: 'Remove limit for {{name}}',
        overflow: 'Your limits go {{amount}} over the budget amount',
        useSum: 'Use the sum: {{amount}}',
        useSumA11y: 'Set the budget amount to {{amount}}, the sum of the limits',
    },
    detail: {
        categoriesCount_one: '{{count}} category',
        categoriesCount_other: '{{count}} categories',
        budgeted: 'Budgeted',
        exceeded: 'Over',
        remaining: 'Remaining',
        editBudget: 'Edit budget',
        transactionsTitle: 'Transactions ({{transactionCount}})',
        emptyTransactions: 'No transactions in this period',
        limitsTitle: 'Subcategory limits',
        limitsOverflow: 'Your limits go {{amount}} over the cap',
        overLimitBy: '{{amount}} over the limit',
        residual: 'Everything else',
        residualAndMore: '{{names}} and {{extra}} more',
    },
    history: {
        range: '{{months}}m',
        averageOf: 'Average · {{range}}',
        ofCap: 'of the cap',
        withinCap: 'Within the cap',
        ofMonths: 'of {{monthCount}} months',
        noClosedCycles: 'No cycle has closed yet. The track record starts once your budget\'s first cycle ends.',
        emptyTitle: 'No history',
        emptySubtitle: 'Create a spending cap to track how many months close within it.',
        score: '{{within}}/{{judged}}',
        chipA11y: '{{budget}} in {{month}}',
        legendWithin: 'within',
        legendOver: 'over',
        legendUntracked: 'no budget',
        legendOpen: 'cycle in progress, through {{date}}',
        detailTitleMonthly: 'Last {{cycleCount}} months',
        detailTitleBiweekly: 'Last {{cycleCount}} fortnights',
        detailTitleWeekly: 'Last {{cycleCount}} weeks',
        averageAndCap: 'average {{average}} · cap {{cap}}',
        capOnly: 'cap {{cap}}',
        overrunMonthly: 'Over the cap in {{overCount}} of the last {{judgedCount}} closed months.',
        overrunBiweekly: 'Over the cap in {{overCount}} of the last {{judgedCount}} closed fortnights.',
        overrunWeekly: 'Over the cap in {{overCount}} of the last {{judgedCount}} closed weeks.',
        suggestion: 'A {{amount}} cap would cover your average.',
        adjust: 'Adjust',
    },
} satisfies DeepPartialStrings<TranslationSchema['budget']>;
